/* global GorgiasChat */

import { Component } from "@theme/component";

class ProductOptionSelectors extends Component {
  constructor() {
    super();
    this.blockId = null;
    this.productId = null;
    this.sectionId = null;
    this.form = null;
    this.variantIdInput = null;
    this.variants = [];
    this.options = [];
    this.variantData = null;
    this.isInitialized = false;
    this.addToCartComponents = [];
    this.defaultOptionValues = [];
    this.productType = null;
    this.hydrationAbortController = null;
    this.currentProductUrl = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.isInitialized = false;
    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.blockId = this.dataset.blockId;
    if (!this.blockId) return;

    const productIdAttr = this.dataset.productId;
    if (productIdAttr) {
      this.productId = parseInt(productIdAttr, 10);
    }

    this.productType = this.dataset.productType || "";

    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) {
      const section = this.closest(".shopify-section");
      if (section) {
        const foundId = section.id;
        this.sectionId = foundId.startsWith("shopify-section-") ? foundId.replace("shopify-section-", "") : foundId;
      }
    } else {
      if (this.sectionId.startsWith("shopify-section-")) {
        this.sectionId = this.sectionId.replace("shopify-section-", "");
      }
    }

    // [OPTIMIZATION] Find linked Add to Cart components
    // 1. Specific Match: Buttons linked directly to this product (e.g. Sticky Bar)
    const productMatches = this.productId
      ? Array.from(document.querySelectorAll(`product-add-to-cart[data-product-id="${this.productId}"]`))
      : [];

    // 2. Section Match: Shared buttons in the same section (e.g. Bundle ATC)
    const sectionMatches = this.sectionId
      ? Array.from(document.querySelectorAll(`product-add-to-cart[data-section-id="${this.sectionId}"]`))
      : [];

    // 3. Bundle Mode Match: Find any bundle-mode add-to-cart buttons on the page
    // This enables cross-section communication for bundle pages where option selectors
    // are in different sections than the shared add-to-cart button
    const bundleModeMatches = Array.from(document.querySelectorAll("product-add-to-cart[data-items]"));

    // Merge unique components
    const allTargets = new Set([...productMatches, ...sectionMatches, ...bundleModeMatches]);
    this.addToCartComponents = Array.from(allTargets);

    this.form = this.querySelector("[data-variant-picker-form]");
    if (!this.form) return;

    if (this.form.dataset.variantSelectorInitialized === "true") return;
    this.form.dataset.variantSelectorInitialized = "true";

    this.variantIdInput = this.form.querySelector("[data-variant-id-input]");
    if (!this.variantIdInput) return;

    const variantDataResult = this._parseJsonData(this.form, "[data-variant-data]");
    if (variantDataResult === false) return;
    if (variantDataResult !== null) this.variantData = variantDataResult;

    const variantsResult = this._parseJsonData(this.form, "[data-all-variants-data]");
    if (variantsResult === false) return;
    if (variantsResult !== null) this.variants = variantsResult;

    const optionsResult = this._parseJsonData(this, "[data-product-options]");
    if (optionsResult === false) return;
    if (optionsResult !== null) this.options = optionsResult;

    // Parse default option values from blocks
    this.defaultOptionValues = this._parseDefaultOptionValuesFromBlocks();
    if (this.defaultOptionValues.length > 0) {
      // Save defaults to localStorage on first load
      this.saveDefaultValuesToLocalStorage();
    }

    this.setupEventListeners();
    this.initializeForm();
    this.initializeBootWidthDetails();

    const totalVariants = parseInt(this.form.dataset.totalVariants || "0", 10);
    const newProductUrl = this.form.dataset.productUrl;

    if (this.currentProductUrl !== newProductUrl) {
      if (this.hydrationAbortController) {
        this.hydrationAbortController.abort();
      }
      this.currentProductUrl = newProductUrl;
    }

    if (this.variants.length > 0 && this.variants.length < totalVariants) {
      this.fetchRemainingVariants(totalVariants, newProductUrl);
    }

    this.isInitialized = true;
  }

  async fetchRemainingVariants(totalVariants, productUrl) {
    if (!productUrl) return;

    this.hydrationAbortController = new AbortController();
    const signal = this.hydrationAbortController.signal;
    const pageSize = 250;
    const pagesNeeded = Math.ceil(totalVariants / pageSize);
    const seenIds = new Set(this.variants.map(varRow => varRow.id));

    try {
      for (let pageIdx = 2; pageIdx <= pagesNeeded; pageIdx++) {
        const reqUrl = `${productUrl}?section_id=product-variants-api&page=${pageIdx}`;
        const res = await fetch(reqUrl, { signal });
        if (!res.ok) continue;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const scriptEl = doc.querySelector("[data-api-variants]");
        if (!scriptEl) continue;
        const batch = JSON.parse(scriptEl.textContent);
        if (!Array.isArray(batch)) continue;
        for (const variantRow of batch) {
          const rowId = variantRow?.id;
          if (rowId != null && !seenIds.has(rowId)) {
            seenIds.add(rowId);
            this.variants.push(variantRow);
          }
        }
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("[Product Option Selectors] Error fetching remaining variants:", e);
      return;
    }

    this.updateOptionVisibility();
    this.ensureAvailableVariantSelection();
    this.updateSelectedStates();
    this.updateOptionNameDisplays();
    this.updateVariantId();
  }

  _parseJsonData(container, selector) {
    const script = container.querySelector(selector);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error(`[Product Option Selectors] Error parsing JSON data from ${selector}:`, e);
      return false;
    }
  }

  _parseDefaultOptionValuesFromBlocks() {
    const defaultValues = [];
    const scripts = this.querySelectorAll('script[type="application/json"][data-default-option-value]');

    scripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent);
        if (data.optionName && data.value) {
          defaultValues.push({
            optionName: data.optionName,
            value: data.value,
          });
        }
      } catch (e) {
        console.error(`[Product Option Selectors] Error parsing default option value from block:`, e);
      }
    });

    return defaultValues;
  }

  getLocalStorageKey(optionName) {
    return `productOption_${this.productType}_${optionName}`;
  }

  getSavedOptionValue(optionName) {
    if (!this.productType || !optionName) return null;
    try {
      const key = this.getLocalStorageKey(optionName);
      return localStorage.getItem(key);
    } catch (e) {
      console.error(`[Product Option Selectors] Error reading from localStorage:`, e);
      return null;
    }
  }

  saveOptionValue(optionName, value) {
    if (!this.productType || !optionName) return;
    try {
      const key = this.getLocalStorageKey(optionName);
      localStorage.setItem(key, value);
    } catch (e) {
      console.error(`[Product Option Selectors] Error saving to localStorage:`, e);
    }
  }

  saveDefaultValuesToLocalStorage() {
    if (!Array.isArray(this.defaultOptionValues) || this.defaultOptionValues.length === 0) return;
    if (!this.productType) return;

    this.defaultOptionValues.forEach(defaultVal => {
      if (defaultVal.optionName && defaultVal.value) {
        const key = this.getLocalStorageKey(defaultVal.optionName);
        // Only save if no existing value exists
        if (!localStorage.getItem(key)) {
          try {
            localStorage.setItem(key, defaultVal.value);
          } catch (e) {
            console.error(`[Product Option Selectors] Error saving default value to localStorage:`, e);
          }
        }
      }
    });
  }

  findOptionPositionByName(optionName) {
    if (!optionName || !this.options) return null;
    const option = this.options.find(opt => opt.name.toLowerCase() === optionName.toLowerCase());
    return option?.position?.toString() || null;
  }

  findInputByOptionValue(optionPosition, value) {
    if (!this.form || !optionPosition || !value) return null;
    const inputs = this.form.querySelectorAll(
      `input[type="radio"][data-option-position="${optionPosition}"]:not([data-non-variant-option])`
    );
    return Array.from(inputs).find(input => input.value === value || input.dataset.optionValue === value) || null;
  }

  findNextAvailableVariant(optionPosition, preferredValue) {
    if (!this.form || !optionPosition) return null;

    const inputs = this.form.querySelectorAll(
      `input[type="radio"][data-option-position="${optionPosition}"]:not([data-non-variant-option])`
    );
    const visibleInputs = Array.from(inputs).filter(input => {
      const label = input.closest("label");
      return label && label.style.display !== "none" && !label.classList.contains("hidden");
    });

    // Try preferred value first
    if (preferredValue) {
      const preferredInput = visibleInputs.find(
        input => input.value === preferredValue || input.dataset.optionValue === preferredValue
      );
      if (preferredInput) {
        const isAvailable = preferredInput.dataset.optionAvailable === "true";
        if (isAvailable) {
          return preferredInput;
        }
        // Preferred value is unavailable, find next available variant after it
        const preferredIdx = visibleInputs.indexOf(preferredInput);
        // Start searching from the next item after preferred value
        for (let i = preferredIdx + 1; i < visibleInputs.length; i++) {
          if (visibleInputs[i].dataset.optionAvailable === "true") {
            return visibleInputs[i];
          }
        }
        // If no available variant found after preferred, wrap around and search from beginning
        for (let i = 0; i < preferredIdx; i++) {
          if (visibleInputs[i].dataset.optionAvailable === "true") {
            return visibleInputs[i];
          }
        }
      }
    }

    // If no preferred value or preferred not found, find first available variant
    const availableInput = visibleInputs.find(input => input.dataset.optionAvailable === "true");
    if (availableInput) {
      return availableInput;
    }

    // If no available variant, return first visible input (unavailable but keeps selection)
    return visibleInputs[0] || null;
  }

  applySavedOrDefaultValues() {
    if (!this.form || !this.options) return;

    const appliedSelections = {};
    // Process options in order (position 1, 2, 3, etc.)
    const sortedOptions = [...this.options].sort((a, b) => a.position - b.position);

    // Get color option position to skip it
    const colorOptionPosition = this.getColorOptionPosition();

    sortedOptions.forEach(option => {
      const optionName = option.name;
      const optionPosition = option.position.toString();

      // Skip color option - don't apply saved/default values to color
      if (colorOptionPosition && optionPosition === String(colorOptionPosition)) {
        return; // Skip color option
      }

      // Priority 1: Check localStorage for saved value
      let targetValue = this.getSavedOptionValue(optionName);

      // Priority 2: If no saved value, check default values
      if (!targetValue && Array.isArray(this.defaultOptionValues)) {
        const defaultVal = this.defaultOptionValues.find(
          dv => dv.optionName && dv.optionName.toLowerCase() === optionName.toLowerCase()
        );
        if (defaultVal && defaultVal.value) {
          targetValue = defaultVal.value;
        }
      }

      // If we have a target value, try to apply it
      if (targetValue) {
        const targetInput = this.findInputByOptionValue(optionPosition, targetValue);
        if (targetInput) {
          // Set the value - updateOptionVisibility will handle visibility and reset if needed
          targetInput.checked = true;
          appliedSelections[optionPosition] = targetValue;
          // Mark this as a saved/default selection to preserve it during visibility updates
          targetInput.dataset.savedSelection = "true";
        }
      }
    });

    // Update UI states after applying selections
    if (Object.keys(appliedSelections).length > 0) {
      this.updateSelectedStates();
      this.updateOptionNameDisplays();
    }
  }

  ensureAvailableVariantSelection() {
    if (!this.form || !this.options) return;

    const selectedOptions = this.getSelectedOptions();
    const variantId = this.findVariantId(selectedOptions);
    const variant = variantId ? this.variants.find(v => v.id === variantId) : null;

    // If variant exists but is unavailable, try to find next available variant
    if (variant && !variant.available) {
      // Try to find an available variant by checking options in order
      // Start from the last option and work backwards to preserve higher priority selections
      const sortedOptions = [...this.options].sort((a, b) => b.position - a.position);

      for (const option of sortedOptions) {
        const optionPosition = option.position.toString();
        const currentValue = selectedOptions[optionPosition];

        if (currentValue) {
          const currentInput = this.findInputByOptionValue(optionPosition, currentValue);
          if (currentInput) {
            const isAvailable = currentInput.dataset.optionAvailable === "true";

            if (!isAvailable) {
              // Find next available variant for this option
              const nextAvailable = this.findNextAvailableVariant(optionPosition, currentValue);
              if (nextAvailable && nextAvailable.value !== currentValue) {
                nextAvailable.checked = true;
                // Do NOT update localStorage here - only update when user interacts with selectors

                // Re-check variant availability after change
                const newSelectedOptions = this.getSelectedOptions();
                const newVariantId = this.findVariantId(newSelectedOptions);
                const newVariant = newVariantId ? this.variants.find(v => v.id === newVariantId) : null;

                if (newVariant && newVariant.available) {
                  // Found available variant, update UI and stop
                  this.updateOptionVisibility();
                  this.updateSelectedStates();
                  this.updateOptionNameDisplays();
                  this.updateVariantId(optionPosition);
                  return;
                }
              }
            }
          }
        }
      }

      // If we get here, no available variant was found
      // Keep the current selection (product is unavailable)
      this.updateSelectedStates();
      this.updateOptionNameDisplays();
    }
  }

  setupEventListeners() {
    this._onChange = event => {
      this.handleOptionChange(event);
    };
    this._onInput = event => {
      if (event.target.type === "radio") this.handleOptionChange(event);
    };
    this._onClick = event => {
      const genderButton = event.target.closest("[data-gender]");
      if (genderButton) {
        this.handleGenderToggle(genderButton);
        return;
      }

      const sizeGuideTrigger = event.target.closest("[data-size-guide-trigger]");
      if (sizeGuideTrigger) {
        this.handleSizeGuideTrigger(sizeGuideTrigger);
        return;
      }

      const gorgiasChatBtn = event.target.closest("[data-open-gorgias-chat]");
      if (gorgiasChatBtn) {
        this.handleGorgiasChatFromSizeGuide(gorgiasChatBtn);
        return;
      }
    };

    this.form.addEventListener("change", this._onChange);
    this.form.addEventListener("input", this._onInput);
    this.addEventListener("click", this._onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.form) {
      if (this._onChange) this.form.removeEventListener("change", this._onChange);
      if (this._onInput) this.form.removeEventListener("input", this._onInput);
    }
    if (this._onClick) this.removeEventListener("click", this._onClick);

    if (this._bootWidthVideoCleanup) {
      this._bootWidthVideoCleanup.forEach(cleanup => cleanup());
      this._bootWidthVideoCleanup = [];
    }

    this._onChange = null;
    this._onInput = null;
    this._onClick = null;
    this.addToCartComponents = [];
  }

  findVariantId(selectedOptions) {
    const { variants, options } = this;
    const variantOptions = Object.fromEntries(Object.entries(selectedOptions).filter(([key]) => key !== "boot-width"));

    const matchingVariant = variants.find(variant => {
      return options.every((option, i) => {
        const optionPosition = option.position.toString();
        const optionValue = variant.options[i];
        return variantOptions[optionPosition] === optionValue;
      });
    });

    return matchingVariant?.id ?? null;
  }

  getSelectedOptions() {
    const selectedOptions = {};
    const inputs = this.form.querySelectorAll('input[type="radio"]:checked:not([data-non-variant-option])');
    inputs.forEach(input => {
      const position = input.dataset.optionPosition;
      if (position) {
        selectedOptions[position] = input.value;
      }
    });
    return selectedOptions;
  }

  updateVariantId(changedOptionPosition = null) {
    const { variantIdInput, variants, sectionId, productId } = this;
    const selectedOptions = this.getSelectedOptions();
    const variantId = this.findVariantId(selectedOptions);
    const variant = variantId ? variants.find(v => v.id === variantId) : null;

    if (variantId) {
      variantIdInput.value = variantId;

      // Update URL with variant ID ONLY if not inside a bundle-showcase context
      const isInBundleShowcase = this.closest("bundle-showcase-component");

      if (!isInBundleShowcase) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variantId);

        if (url.href !== window.location.href) {
          window.history.replaceState({}, "", url.toString());
        }
      }
    }

    // Bundle `data-items` / button price must update before `variant:changed` so listeners (e.g. loyalty earn) read current totals.
    this.updateAddToCartComponent(variant);

    if (variantId) {
      const eventDetail = {
        variant,
        variantId,
        sectionId,
        productId: variant?.product_id ?? productId,
        changedOptionPosition,
      };

      const event = new CustomEvent("variant:changed", {
        detail: eventDetail,
        bubbles: true,
        cancelable: true,
      });
      document.dispatchEvent(event);
    }

    this.updateSoldOutRedirectVisibility(variant);
  }

  updateSoldOutRedirectVisibility(variant = null) {
    const el = this.querySelector("[data-sold-out-redirect]");
    if (!el) return;
    const isSoldOut = variant != null && variant.available === false;
    el.classList.toggle("hidden", !isSoldOut);
  }

  /**
   * Stable key for bundle `data-items` slots. `block.id` alone is not safe: each
   * `bundle-showcase` section uses the same static block id (`product-option-selectors`),
   * so Liquid `block.id` can repeat across sections — the third+ showcase would overwrite
   * an earlier slot and the ATC price would only sum two lines.
   * @returns {string}
   */
  getBundleItemTrackingId() {
    return `${this.sectionId ?? ""}:${this.blockId}`;
  }

  updateAddToCartComponent(variant = null) {
    if (!this.addToCartComponents || this.addToCartComponents.length === 0) return;

    if (!variant) {
      const selectedOptions = this.getSelectedOptions();
      const variantId = this.findVariantId(selectedOptions);
      if (variantId) {
        variant = this.variants.find(v => v.id === variantId);
      }
    }

    const isWomenSelected = Array.from(this.querySelectorAll('[data-gender="women"]')).some(btn =>
      btn.classList.contains("productOptions__genderTab--isActive")
    );

    const properties = {};
    if (isWomenSelected) {
      properties._women_size_selected = true;
    }
    const propertiesJson = JSON.stringify(properties);

    // Flag to explicitly clear the property when men is selected
    const shouldClearWomenProperty = !isWomenSelected;

    // Get sold_out tag status from data attribute
    const hasSoldOutTag = this.dataset.soldOut === "true";

    this.addToCartComponents.forEach(component => {
      // [BUNDLE MODE] Shared button with data-items array
      if (component.hasAttribute("data-items")) {
        let items = [];
        try {
          items = JSON.parse(component.dataset.items);
        } catch (e) {
          console.error(`[Product Option Selectors] Error parsing JSON data from ${component.dataset.items}:`, e);
        }

        const bundleItemTrackingId = this.getBundleItemTrackingId();
        // Prefer composite key; migrate legacy entries keyed only by block.id (same id across bundle sections).
        let itemIndex = items.findIndex(i => i._selector_id === bundleItemTrackingId);
        if (itemIndex === -1) {
          itemIndex = items.findIndex(i => i._selector_id === this.blockId && i.product_id === this.productId);
        }

        if (itemIndex === -1) {
          itemIndex = items.length;
          items.push({
            product_id: this.productId,
            _selector_id: bundleItemTrackingId,
          });
        } else {
          items[itemIndex]._selector_id = bundleItemTrackingId;
        }

        if (variant) {
          items[itemIndex].id = variant.id;
          items[itemIndex].price = variant.price;
          items[itemIndex].compare_at_price = variant.compare_at_price;
          items[itemIndex].available = variant.available;
          items[itemIndex].inventory_policy = variant.inventory_policy;
          items[itemIndex].inventory_quantity = variant.inventory_quantity;
          items[itemIndex].inventory_management = variant.inventory_management || null;
          items[itemIndex].requires_selling_plan = variant.requires_selling_plan || false;
          items[itemIndex].has_sold_out_tag = hasSoldOutTag;
          // Merge properties specifically for this item
          const existingProperties = items[itemIndex].properties || {};
          // Explicitly remove _women_size_selected when men is selected
          if (shouldClearWomenProperty) {
            delete existingProperties._women_size_selected;
          }
          items[itemIndex].properties = {
            ...existingProperties,
            ...properties,
          };
        } else {
          items[itemIndex].id = null;
          items[itemIndex].available = false;
        }

        component.dataset.items = JSON.stringify(items);
      }

      // [SINGLE MODE] Specific button for this product
      else if (component.dataset.productId == this.productId) {
        if (variant) {
          component.dataset.selectedVariantId = variant.id;
          component.dataset.price = variant.price;
          component.dataset.compareAtPrice = variant.compare_at_price || "";
          component.dataset.inventoryPolicy = variant.inventory_policy;
          component.dataset.inventoryQuantity = variant.inventory_quantity;
          component.dataset.inventoryManagement = variant.inventory_management || "";
        }
        component.dataset.cartProperties = propertiesJson;
      }
    });
  }

  findVariantForOptions(testOptions) {
    const { variants, options } = this;
    const variantOptions = Object.fromEntries(Object.entries(testOptions).filter(([key]) => key !== "boot-width"));

    return (
      variants.find(variant => {
        return options.every((option, i) => {
          const optionPosition = option.position.toString();
          if (!(optionPosition in variantOptions)) return true;
          const optionValue = variant.options[i];
          return variantOptions[optionPosition] === optionValue;
        });
      }) ?? null
    );
  }

  findAllVariantsForOptions(testOptions) {
    const { variants, options } = this;
    const variantOptions = Object.fromEntries(Object.entries(testOptions).filter(([key]) => key !== "boot-width"));

    return variants.filter(variant => {
      return options.every((option, i) => {
        const optionPosition = option.position.toString();
        if (!(optionPosition in variantOptions)) return true;
        const optionValue = variant.options[i];
        return variantOptions[optionPosition] === optionValue;
      });
    });
  }

  getColorOptionPosition() {
    const { options } = this;
    const colorOption = options.find(option => option.name.toLowerCase() === "color");
    return colorOption?.position ?? null;
  }

  updateOptionVisibility() {
    const { form } = this;
    const colorOptionPosition = this.getColorOptionPosition();
    const inputsByPosition = {};
    const allInputs = form.querySelectorAll('input[type="radio"]:not([data-non-variant-option])');

    allInputs.forEach(input => {
      const position = input.dataset.optionPosition;
      if (!position) return;
      if (!inputsByPosition[position]) inputsByPosition[position] = [];
      inputsByPosition[position].push(input);
    });

    const sortedPositions = Object.keys(inputsByPosition).sort((a, b) => parseInt(a) - parseInt(b));
    let currentSelections = this.getSelectedOptions();

    sortedPositions.forEach(position => {
      if (position === colorOptionPosition?.toString()) return;

      const positionNum = parseInt(position);
      const inputs = inputsByPosition[position];
      let hasVisibleSelected = false;
      let firstVisibleInput = null;

      currentSelections = this.getSelectedOptions();

      inputs.forEach(input => {
        const label = input.closest("label");
        if (!label) return;

        const testOptions = Object.fromEntries(
          Object.entries(currentSelections).filter(([selPosition]) => {
            const selPosNum = parseInt(selPosition);
            return selPosNum < positionNum;
          })
        );

        testOptions[position] = input.value;

        const variant = this.findVariantForOptions(testOptions);
        const isValid = variant !== null;

        // For visibility, use partial combination (testOptions)
        // For availability of checked inputs, use full current selections to get accurate availability
        let isVariantAvailable = false;
        if (input.checked) {
          // If this is the currently selected option, check availability with full selections
          const fullVariant = this.findVariantForOptions(currentSelections);
          isVariantAvailable = fullVariant?.available === true;
        } else {
          // For unselected options, check ALL variants that match current selections from other options + this option value
          // Check ALL variants that match the partial combination (current selections from previous options + this option value)
          // This ensures we check if ALL variants with this option value AND the current selections are out of stock
          const allMatchingVariants = this.findAllVariantsForOptions(testOptions);

          // Mark as OOS only if ALL matching variants are out of stock
          if (allMatchingVariants.length > 0) {
            const allUnavailable = allMatchingVariants.every(v => !v.available);
            isVariantAvailable = !allUnavailable;
          } else {
            // No matching variants found
            isVariantAvailable = false;
          }
        }

        if (isValid) {
          label.classList.remove("hidden");
          label.style.display = "";
          if (!firstVisibleInput) firstVisibleInput = input;
          if (input.checked) hasVisibleSelected = true;
        } else {
          label.classList.add("hidden");
          label.style.display = "none";
        }

        input.dataset.optionAvailable = isVariantAvailable.toString();
      });

      if (!hasVisibleSelected && firstVisibleInput) {
        firstVisibleInput.checked = true;
        currentSelections[position] = firstVisibleInput.value;
        this.updateSelectedStates();
        this.updateOptionNameDisplays();
      }

      // Clear saved selection markers after visibility check is complete
      inputs.forEach(input => {
        if (input.dataset.savedSelection === "true") {
          delete input.dataset.savedSelection;
        }
      });
    });

    this.updateGenderToggleDisplay();
  }

  updateGenderToggleDisplay() {
    const { form } = this;
    const genderSelectors = this.querySelectorAll("[data-gender-selector]");
    genderSelectors.forEach(selector => {
      const optionPosition = selector.dataset.optionPosition;
      if (!optionPosition) return;

      const activeButton = selector.querySelector(".productOptions__genderTab--isActive");
      if (activeButton) {
        const gender = activeButton.dataset.gender;
        const sizeContainer = form.querySelector(
          `[data-size-options-container][data-option-position="${optionPosition}"]`
        );
        if (sizeContainer) {
          const optionInputs = sizeContainer.querySelectorAll(`input[data-option-position="${optionPosition}"]`);

          optionInputs.forEach(input => {
            const label = input.closest("label");
            if (!label || label.style.display === "none") return;

            const textSpan = label.querySelector(".productOptions__valueText");
            if (!textSpan) return;

            const originalText = textSpan.dataset.originalText ?? input.value;
            const originalValue = parseFloat(originalText);

            if (!isNaN(originalValue)) {
              if (gender === "women") {
                const womensSize = originalValue + 1.5;
                textSpan.textContent = womensSize.toString();
              } else {
                textSpan.textContent = originalText;
              }
            }
          });
        }
      }
    });
  }

  initializeForm() {
    if (this.variantData?.id) {
      this.variantIdInput.value = this.variantData.id;
    }
    this.initializeGenderToggles();
    // Apply saved/default values first (before any visibility updates)
    this.applySavedOrDefaultValues();
    // Update visibility - this will handle showing/hiding options based on our selections
    this.updateOptionVisibility();
    // Ensure selected variant is available, or find next available
    this.ensureAvailableVariantSelection();
    this.updateSelectedStates();
    this.updateOptionNameDisplays();
    this.updateVariantId();
    this.updateAddToCartComponent();
  }

  initializeGenderToggles() {
    const { form } = this;
    const genderSelectors = this.querySelectorAll("[data-gender-selector]");
    genderSelectors.forEach(selector => {
      const optionPosition = selector.dataset.optionPosition;
      if (!optionPosition) return;

      const menButton = selector.querySelector('[data-gender="men"]');
      const womenButton = selector.querySelector('[data-gender="women"]');
      if (menButton && womenButton) {
        menButton.classList.add("productOptions__genderTab--isActive");
        menButton.setAttribute("aria-pressed", "true");
        womenButton.classList.remove("productOptions__genderTab--isActive");
        womenButton.setAttribute("aria-pressed", "false");
      }

      const sizeContainer = form.querySelector(
        `[data-size-options-container][data-option-position="${optionPosition}"]`
      );
      if (sizeContainer) {
        const optionInputs = sizeContainer.querySelectorAll(`input[data-option-position="${optionPosition}"]`);
        optionInputs.forEach(input => {
          const label = input.closest("label");
          if (!label) return;

          const textSpan = label.querySelector(".productOptions__valueText");
          if (textSpan && !textSpan.dataset.originalText) {
            textSpan.dataset.originalText = textSpan.textContent.trim();
          }
        });
      }
    });
  }

  updateSelectedStates() {
    const { form } = this;
    const allInputs = form.querySelectorAll('input[type="radio"]');
    allInputs.forEach(input => {
      const label = input.closest("label");
      if (!label) return;

      const isSelected = input.checked;
      const isNonVariantOption =
        input.hasAttribute("data-non-variant-option") || input.dataset.optionPosition === "boot-width";

      if (label.classList.contains("productOptions__value")) {
        label.classList.toggle("productOptions__value--isSelected", isSelected);

        if (!isNonVariantOption) {
          const isAvailable = input.dataset.optionAvailable === "true";
          label.classList.toggle("productOptions__value--isUnavailable", !isAvailable);
        }

        const textSpan = label.querySelector(".productOptions__valueText");
        if (textSpan) {
          textSpan.classList.toggle("productOptions__valueText--isSelected", isSelected);
        }
      }

      if (label.classList.contains("productOptions__swatch")) {
        label.classList.toggle("productOptions__swatch--isSelected", isSelected);
        label.classList.toggle("swatch-selected", isSelected);
      }
    });
  }

  updateOptionNameDisplays() {
    const { form } = this;
    const selectedOptions = this.getSelectedOptions();

    // Check current gender selection
    const isWomenSelected = Array.from(this.querySelectorAll('[data-gender="women"]')).some(btn =>
      btn.classList.contains("productOptions__genderTab--isActive")
    );
    const genderPrefix = isWomenSelected ? "Women's" : "Men's";

    // Size mappings for Sock and Insole products
    const sockSizeMap = {
      M: "M (US shoe size 6-8.5)",
      L: "L (US shoe size 9-11.5)",
      XL: "XL (US shoe size 12-16)",
    };

    const insoleSizeMap = {
      XS: "XS (Men's US Shoe Size 4.5-5.5)",
      S: "S (Men's US Shoe Size 6-7)",
      M: "M (Men's US Shoe Size 7.5-8.5)",
      L: "L (Men's US Shoe Size 9-10)",
      XL: "XL (Men's US Shoe Size 10.5-11.5)",
      "2XL": "2XL (Men's US Shoe Size 12-13)",
      "3XL": "3XL (Men's US Shoe Size 14-16)",
    };

    const productType = this.dataset.productType;

    Object.keys(selectedOptions).forEach(position => {
      const selectedValue = selectedOptions[position];
      const displayElement = form.querySelector(`[data-option-selected-value][data-option-position="${position}"]`);

      if (displayElement && selectedValue) {
        // Check if this option has a gender toggle (size options with men/women tabs)
        const hasGenderToggle = displayElement.dataset.hasGenderToggle === "true";

        // Check if this is a size option (position 1 or 2 typically)
        const optionName = form.querySelector(`[data-option-position="${position}"] input`)?.name?.toLowerCase() || "";
        const isSizeOption = optionName.includes("size");

        if (isSizeOption && productType === "Sock") {
          displayElement.textContent = sockSizeMap[selectedValue] || selectedValue;
        } else if (isSizeOption && productType === "Insole") {
          displayElement.textContent = insoleSizeMap[selectedValue] || selectedValue;
        } else if (hasGenderToggle) {
          // For size options with gender toggle, get the displayed value (which may be converted for women)
          const sizeContainer = form.querySelector(`[data-size-options-container][data-option-position="${position}"]`);
          const checkedInput = sizeContainer?.querySelector(`input[data-option-position="${position}"]:checked`);
          const textSpan = checkedInput?.closest("label")?.querySelector(".productOptions__valueText");
          const displayedSize = textSpan?.textContent?.trim() || selectedValue;
          displayElement.textContent = `${genderPrefix} ${displayedSize}`;
        } else {
          displayElement.textContent = selectedValue;
        }
      }
    });

    const bootWidthInput = form.querySelector('input[data-option-position="boot-width"]:checked');
    if (bootWidthInput) {
      const bootWidthDisplay = form.querySelector('[data-option-selected-value][data-option-position="boot-width"]');
      if (bootWidthDisplay) {
        bootWidthDisplay.textContent = bootWidthInput.value;
      }
    }

    const allDisplayElements = this.form.querySelectorAll("[data-option-selected-value]");
    allDisplayElements.forEach(element => {
      const position = element.dataset.optionPosition;
      if (position === "boot-width") return;
      if (!selectedOptions[position]) {
        element.textContent = "Selection";
      }
    });
  }

  handleOptionChange(event) {
    const input = event.target;
    if (!input || input.type !== "radio") return;

    const { form } = this;
    const optionName = input.name;
    const sameGroupInputs = form.querySelectorAll(`input[type="radio"][name="${optionName}"]`);

    sameGroupInputs.forEach(radio => {
      const label = radio.closest("label");
      if (!label) return;

      const isSelected = radio.checked;
      const isNonVariantOption =
        radio.hasAttribute("data-non-variant-option") || radio.dataset.optionPosition === "boot-width";

      if (label.classList.contains("productOptions__value")) {
        label.classList.toggle("productOptions__value--isSelected", isSelected);

        if (!isNonVariantOption) {
          const isAvailable = radio.dataset.optionAvailable === "true";
          label.classList.toggle("productOptions__value--isUnavailable", !isAvailable);
        }

        const textSpan = label.querySelector(".productOptions__valueText");
        if (textSpan) {
          textSpan.classList.toggle("productOptions__valueText--isSelected", isSelected);
        }
      }

      if (label.classList.contains("productOptions__swatch")) {
        label.classList.toggle("productOptions__swatch--isSelected", isSelected);
        label.classList.toggle("swatch-selected", isSelected);
      }
    });

    this.updateOptionNameDisplays();

    // Handle boot width changes
    if (input.dataset.optionPosition === "boot-width") {
      this.updateBootWidthAdjustableText();
    }

    if (input.hasAttribute("data-non-variant-option")) return;

    // Save to localStorage when user interacts with option
    const optionPosition = input.dataset.optionPosition;
    if (optionPosition) {
      const option = this.options.find(opt => opt.position.toString() === optionPosition);
      if (option && option.name) {
        this.saveOptionValue(option.name, input.value);
      }
    }

    const changedOptionPosition = input.dataset.optionPosition;
    this.updateOptionVisibility();
    this.updateVariantId(changedOptionPosition);
    this.updateSelectedStates();
    this.updateOptionNameDisplays();
  }

  handleGenderToggle(genderButton) {
    const { form } = this;
    const gender = genderButton.dataset.gender;
    const optionPosition = genderButton.dataset.optionPosition;
    if (!optionPosition) return;

    const genderSelector = genderButton.closest("[data-gender-selector]");
    if (!genderSelector) return;

    const menButton = genderSelector.querySelector('[data-gender="men"]');
    const womenButton = genderSelector.querySelector('[data-gender="women"]');

    if (menButton && womenButton) {
      if (gender === "men") {
        menButton.classList.add("productOptions__genderTab--isActive");
        menButton.setAttribute("aria-pressed", "true");
        womenButton.classList.remove("productOptions__genderTab--isActive");
        womenButton.setAttribute("aria-pressed", "false");
      } else {
        womenButton.classList.add("productOptions__genderTab--isActive");
        womenButton.setAttribute("aria-pressed", "true");
        menButton.classList.remove("productOptions__genderTab--isActive");
        menButton.setAttribute("aria-pressed", "false");
      }
    }

    const sizeContainer = form.querySelector(`[data-size-options-container][data-option-position="${optionPosition}"]`);
    if (!sizeContainer) return;

    const optionInputs = sizeContainer.querySelectorAll(`input[data-option-position="${optionPosition}"]`);

    optionInputs.forEach(input => {
      const label = input.closest("label");
      if (!label) return;

      const textSpan = label.querySelector(".productOptions__valueText");
      if (!textSpan) return;

      const originalText = textSpan.dataset.originalText ?? input.value;
      const originalValue = parseFloat(originalText);

      if (!isNaN(originalValue)) {
        if (gender === "women") {
          const womensSize = originalValue + 1.5;
          textSpan.textContent = womensSize.toString();
        } else {
          textSpan.textContent = originalText;
        }
      }
    });

    this.updateOptionNameDisplays();
    this.updateAddToCartComponent();
  }

  handleSizeGuideTrigger(trigger) {
    const sizeGuideId = trigger.dataset.sizeGuideId;
    const optionName = trigger.dataset.optionName;
    const productHandle = trigger.dataset.productHandle;

    // Prefer matching by size-guide-id (product-handle-option-name) for more specific matching
    if (sizeGuideId) {
      const modal = document.querySelector(`.sizeGuide__modal[data-size-guide-id="${sizeGuideId}"]`);
      if (modal && typeof modal.showDialog === "function") {
        modal.showDialog();
        return;
      }
    }

    // Fallback to matching by both product handle and option name
    if (productHandle && optionName) {
      const sizeGuideModals = document.querySelectorAll(
        `.sizeGuide__modal[data-product-handle="${productHandle}"][data-option-name]`
      );

      for (const modal of sizeGuideModals) {
        const modalOptionName = modal.dataset.optionName?.toLowerCase();
        if (modalOptionName === optionName.toLowerCase()) {
          if (typeof modal.showDialog === "function") {
            modal.showDialog();
            return;
          }
        }
      }
    }

    // Legacy fallback: match by option name only (for backwards compatibility)
    if (optionName) {
      const sizeGuideModals = document.querySelectorAll(".sizeGuide__modal[data-option-name]");

      for (const modal of sizeGuideModals) {
        const modalOptionName = modal.dataset.optionName?.toLowerCase();
        if (modalOptionName === optionName.toLowerCase()) {
          if (typeof modal.showDialog === "function") {
            modal.showDialog();
            return;
          }
        }
      }
    }
  }

  /**
   * Opens Gorgias from the size guide chat control and closes the modal.
   * @param {HTMLElement} button - Trigger element (e.g. size guide chat button)
   */
  handleGorgiasChatFromSizeGuide(button) {
    const dialogComp = button.closest("dialog-component");
    if (dialogComp && typeof dialogComp.closeDialog === "function") {
      void dialogComp.closeDialog();
    }

    if (typeof GorgiasChat !== "undefined") {
      GorgiasChat.open();
      GorgiasChat.hidePoweredBy(true);
      const gorgiasClose = document.getElementById("gorgiasClose");
      if (gorgiasClose && window.innerWidth > 992) {
        gorgiasClose.classList.remove("hide");
      }
    }
  }

  initializeBootWidthDetails() {
    const { form } = this;
    if (!form) return;

    // Initialize adjustable width text visibility
    this.updateBootWidthAdjustableText();

    // Set up boot width change listener
    const bootWidthInputs = form.querySelectorAll('input[data-option-position="boot-width"]');
    bootWidthInputs.forEach(input => {
      input.addEventListener("change", () => {
        this.updateBootWidthAdjustableText();
      });
    });

    // Wide feet trigger is now handled by CSS (tooltip) and dialog-component (modal)
    // No JavaScript needed - follows subscription toggle pattern

    // Set up video trigger
    const videoTrigger = this.querySelector("[data-boot-width-video-trigger]");
    if (videoTrigger) {
      this.setupVideoTrigger(videoTrigger);
    }
  }

  updateBootWidthAdjustableText() {
    const { form } = this;
    if (!form) return;

    const bootWidthInput = form.querySelector('input[data-option-position="boot-width"]:checked');
    const adjustableTextElement = this.querySelector("[data-boot-width-adjustable-text]");

    if (!adjustableTextElement) return;

    // Show text only when "Wide (EE)" is selected
    if (bootWidthInput && bootWidthInput.value.includes("Wide")) {
      adjustableTextElement.classList.remove("hidden");
    } else {
      adjustableTextElement.classList.add("hidden");
    }
  }

  setupVideoTrigger(trigger) {
    const videoUrl = trigger.dataset.videoUrl;
    const videoModalId = trigger.dataset.videoModalId;
    if (!videoUrl) return;

    const handleClick = event => {
      event.preventDefault();

      // Find video popup component by ID (video-popup-component is a custom element with id attribute)
      let videoPopup = null;
      if (videoModalId) {
        // Try getElementById first (works for custom elements with id attribute)
        videoPopup = document.getElementById(videoModalId);
        // If not found, try querySelector with tag and id
        if (!videoPopup || videoPopup.tagName !== "VIDEO-POPUP-COMPONENT") {
          videoPopup = document.querySelector(`video-popup-component#${videoModalId}`);
        }
      }

      // Fallback: try to find within this component
      if (!videoPopup) {
        videoPopup = this.querySelector("video-popup-component");
      }

      if (videoPopup && typeof videoPopup.open === "function") {
        videoPopup.open(videoUrl);
      }
    };

    const handleKeyDown = event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick(event);
      }
    };

    trigger.addEventListener("click", handleClick);
    trigger.addEventListener("keydown", handleKeyDown);

    // Clean up on disconnect
    if (!this._bootWidthVideoCleanup) {
      this._bootWidthVideoCleanup = [];
    }
    this._bootWidthVideoCleanup.push(() => {
      trigger.removeEventListener("click", handleClick);
      trigger.removeEventListener("keydown", handleKeyDown);
    });
  }
}

if (!customElements.get("product-option-selectors")) {
  customElements.define("product-option-selectors", ProductOptionSelectors);
}
